import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
    catchError,
    EMPTY,
    map,
    merge,
    Observable,
    shareReplay,
    Subject,
    Subscription,
    switchMap,
    tap,
    withLatestFrom,
} from 'rxjs';
import { UserProfileMin } from 'src/app/authentication/profile/models/user-profile-min';
import { CommunityCard } from 'src/app/card/model/card-implementations/community-card';
import { CardPage } from 'src/app/card/model/card-page';
import {
    createPageableByRouterParameters,
    Pageable,
} from 'src/app/shared/models/pageable';
import { TrainingType } from 'src/app/shared/enums/training-type.enum';
import { ContainerService } from 'src/app/shared/services/container/container.service';
import {
    Container,
    ContainerType,
} from 'src/app/shared/models/container.types';
import { SortBy } from 'src/app/shared/enums/sortby.enum';
import { AuthenticationService } from '../../../authentication/services/authentication.service';

@Component({
    selector: 'app-community-container',
    templateUrl: './community-container.component.html',
    styleUrls: ['./community-container.component.scss'],
    standalone: false,
})
export class CommunityContainerComponent implements OnInit, OnDestroy {
    //HTML import
    protected readonly TrainingType = TrainingType;
    protected readonly ContainerType = ContainerType;

    cardPage: CardPage = new CardPage();

    numberOfPages: number;

    defaultPage = 1;
    defaultPageSize = 6;
    currentFilter: string[] = [];
    searchText: string = '';

    smallHeadlineTranslationKey: string =
        'project.components.communityProject.subtitle';
    headerPicture: string = 'assets/svg/sidebar-nav/gallery-icon.svg';
    showLoadingSpinner: boolean = true;
    resetSearchTrigger$: Subject<void> = new Subject<void>();

    private subscriptions: Subscription[] = [];

    constructor(
        private containerService: ContainerService,
        private router: Router,
        private route: ActivatedRoute,
        protected authService: AuthenticationService
    ) {}

    ngOnInit(): void {
        const queryParams$ = this.route.queryParams.pipe(
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.subscriptions.push(
            merge(
                queryParams$.pipe(
                    map((queryParams) => queryParams)
                ),
                this.authService.activeContextChanged$.pipe(
                    withLatestFrom(queryParams$),
                    map(([, queryParams]) => ({
                        ...queryParams,
                        page: this.defaultPage,
                    }))
                )
            )
                .pipe(switchMap((params) => this.loadContainers(params)))
                .subscribe()
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) =>
            subscription.unsubscribe()
        );
        this.resetSearchTrigger$.complete();
    }

    private loadContainers(params: Params): Observable<void> {
        this.showLoadingSpinner = true;
        this.cardPage.itemCards = [];
        this.cardPage.numberOfItems = 0;
        this.cardPage.numberOfPages = 0;
        this.cardPage.pageable = createPageableByRouterParameters(
            new Pageable(),
            params
        );
        this.cardPage.pageable.pageSize =
            this.cardPage.pageable.pageSize ?? this.defaultPageSize;
        this.searchText = this.cardPage.pageable.searchText ?? '';
        this.syncFilterWithUrlParam();

        return this.containerService
            .getAllCommunityContainers(
                this.getContainerTypesForFilter(),
                this.cardPage.pageable
            )
            .pipe(
                tap((data) => {
                    this.showLoadingSpinner = false;
                    this.populateCards(data.values, data.users);
                    this.cardPage.numberOfItems = data.totalElements;
                    this.cardPage.numberOfPages = data.totalPages;
                }),
                map(() => undefined),
                catchError(() => {
                    this.showLoadingSpinner = false;
                    return EMPTY;
                })
            );
    }

    private populateCards(
        containers: Container[],
        users: UserProfileMin[]
    ): void {
        const userMap = new Map<string, UserProfileMin>();
        containers.forEach((container) => {
            const user =
                userMap.get(container.creator) ??
                users.find((profile) => profile.displayName === container.creator);
            if (user) {
                userMap.set(container.creator, user);
            }
            this.cardPage.itemCards.push(
                CommunityCard.mapToCommunityCard(container, user)
            );
        });
    }

    private syncFilterWithUrlParam(): void {
        if (this.cardPage.pageable.types !== undefined) {
            this.currentFilter = this.cardPage.pageable.types.split(',');
        } else {
            this.cardPage.pageable.types =
                this.currentFilter.length > 0
                    ? this.currentFilter.join(',')
                    : undefined;
        }
    }

    /**
     * Gets the container types array for filtering.
     * If currentFilter is empty, returns all types (show all).
     * Otherwise, maps the filter strings to ContainerType enum values.
     * @returns Array of ContainerType values to filter by
     */
    private getContainerTypesForFilter(): ContainerType[] {
        return this.currentFilter.map(
            (containerTypeAsString) => ContainerType[containerTypeAsString]
        );
    }

    /**
     * Toggles a filter type (adds if not present, removes if present).
     * @param filter - ContainerType string to toggle
     */
    changeFilter(filter: string): void {
        const indexOfType = this.currentFilter.indexOf(filter);
        if (indexOfType !== -1) {
            this.currentFilter.splice(indexOfType, 1);
        } else {
            this.currentFilter.push(filter);
        }
        this.navigateToSelf(
            this.defaultPage,
            this.cardPage.pageable.pageSize,
            this.cardPage.pageable.sortBy,
            this.cardPage.pageable.asc,
            this.currentFilter.length > 0
                ? this.currentFilter.join(',')
                : undefined,
            this.searchText
        );
    }

    /**
     * Removes all active filters and navigates with updated query parameters.
     */
    removeFilter(): void {
        this.currentFilter = [];
        this.navigateToSelf(
            this.defaultPage,
            this.cardPage.pageable.pageSize,
            this.cardPage.pageable.sortBy,
            this.cardPage.pageable.asc,
            undefined,
            this.searchText
        );
    }

    /**
     * Checks if the remove filter button should be disabled.
     * @returns true if no filters are active, false otherwise
     */
    removeFilterButtonDisabled(): boolean {
        return this.currentFilter.length === 0;
    }

    /**
     * Handles search text change from the search component.
     * @param searchText - The new search text (already trimmed)
     */
    onSearchChanged(event: {
        searchText: string;
        sortOption: { sortBy: SortBy; asc: boolean };
    }): void {
        this.searchText = event.searchText;
        this.navigateToSelf(
            this.defaultPage,
            this.cardPage.pageable.pageSize,
            event.sortOption.sortBy,
            event.sortOption.asc,
            this.currentFilter.length > 0
                ? this.currentFilter.join(',')
                : undefined,
            this.searchText
        );
    }

    onSearchCleared(): void {
        this.resetSearchTrigger$.next();
    }

    /**
     * Navigates to the same route with updated query parameters.
     * @param page - Page number
     * @param pageSize - Number of items per page
     * @param sortBy - Sort criteria
     * @param types - Comma-separated list of container types (undefined to show all)
     * @param searchText - Search text (optional)
     */
    private navigateToSelf(
        page: number,
        pageSize: number,
        sortBy: string,
        asc: boolean,
        types: string | undefined,
        searchText?: string
    ): void {
        const queryParams: {
            page: number;
            pageSize: number;
            sortBy?: string;
            types?: string;
            searchText?: string;
            asc?: boolean;
        } = { page, pageSize };
        if (sortBy) {
            queryParams.sortBy = sortBy;
            queryParams.asc = asc;
        }
        if (types) {
            queryParams.types = types;
        }
        if (searchText) {
            queryParams.searchText = searchText;
        }
        this.router.navigate(['/communityprojects'], { queryParams });
    }
}
